import { Box, Chip, Typography } from '@mui/material'
import { FC, ReactNode } from 'react'
import { useLocaleState, useTranslate } from 'react-admin'

import type { StayOperation } from '../../data'
import type { StayDetails as StayDetailsData } from '../../data/source'
import { formatDateTime } from '../../domain/dateTime'

const Item: FC<{ label: string; children: ReactNode }> = ({ label, children }) => (
  <Typography component="span" variant="body2" color="text.secondary">
    {label}: <strong>{children}</strong>
  </Typography>
)

/** One SOSTRAT operation: load/unload, goods and class, tonnes, company, declared window, state. */
const OperationLine: FC<{ operation: StayOperation; index: number }> = ({ operation, index }) => {
  const translate = useTranslate()
  const [locale] = useLocaleState()
  const quantity =
    operation.quantityBasis === 'IMPLAUSIBLE'
      ? translate('pages.scales.stay.implausible', { quantity: operation.quantity?.toLocaleString(locale), unit: operation.unit ?? '' })
      : operation.quantityT === null
        ? null
        : translate('pages.scales.stay.tonnes', { quantity: operation.quantityT.toLocaleString(locale) }) +
          (operation.quantityBasis === 'KG' ? ` (${translate('pages.scales.stay.read_as_kg')})` : '')
  const parts = [
    operation.kind ? translate(`enums.OperationKind.${operation.kind}`) : operation.operation,
    operation.goods &&
      (operation.productClass ? `${operation.goods} (${translate(`enums.ProductClass.${operation.productClass}`)})` : operation.goods),
    quantity,
    operation.terminal,
    operation.windowStart &&
      `${translate('pages.scales.stay.window')} ${formatDateTime(operation.windowStart)} → ${operation.windowEnd ? formatDateTime(operation.windowEnd) : '—'}`,
  ].filter(Boolean)

  return (
    <Typography
      variant="body2"
      color={operation.state === 'Anulado' ? 'text.disabled' : 'text.primary'}
      sx={{ textDecoration: operation.state === 'Anulado' ? 'line-through' : undefined }}
      data-testid={`stay-operation-${String(index)}`}
    >
      {parts.join(' · ')}
    </Typography>
  )
}

/** Whether SOSTRAT reports anything about a berth stay (not, e.g., about a manual stay). */
export const hasStayDetails = (stay: StayDetailsData): boolean =>
  stay.sostratState !== null ||
  stay.fondeoPrevio !== null ||
  stay.anterior !== null ||
  stay.muellesAlternativos.length > 0 ||
  stay.sostratTerminal !== null ||
  stay.operations.length > 0

/** What SOSTRAT reports about a berth stay; nothing when it reports nothing (see `hasStayDetails`). */
export const StayDetails: FC<{ stay: StayDetailsData }> = ({ stay }) => {
  const translate = useTranslate()
  if (!hasStayDetails(stay)) return null

  return (
    <Box display="flex" flexDirection="column" gap={0.5} mt={0.5} data-testid="stay-details">
      <Box display="flex" flexWrap="wrap" columnGap={2} rowGap={0.5} alignItems="center">
        {stay.sostratState && <Chip size="small" variant="outlined" label={stay.sostratState} />}
        {stay.fondeoPrevio !== null && (
          <Item label={translate('pages.scales.stay.fondeo_previo')}>
            {translate(stay.fondeoPrevio ? 'pages.scales.stay.yes' : 'pages.scales.stay.no')}
          </Item>
        )}
        {stay.anterior && <Item label={translate('pages.scales.stay.anterior')}>{stay.anterior}</Item>}
        {stay.muellesAlternativos.length > 0 && (
          <Item label={translate('pages.scales.stay.alternativos')}>{stay.muellesAlternativos.join(', ')}</Item>
        )}
        {stay.sostratTerminal && (
          <Item label={translate('pages.scales.stay.sostrat_terminal')}>
            {[stay.sostratTerminal, stay.sostratTermAdd].filter(Boolean).join(', ')}
          </Item>
        )}
      </Box>
      {stay.operations.map((operation, index) => (
        <OperationLine key={index} operation={operation} index={index} />
      ))}
    </Box>
  )
}
